import type { CommandAction, KeyChord } from "./commands.types";

/** Same fields S7's history hotkeys leave alone. */
export const FIELD_SELECTOR = "input, textarea, select, [contenteditable], .cm-editor, [data-history-ignore]";
/** Same as S7's hotkey guard: a modal, or any open dialog (popovers included). */
const MODAL_SELECTOR = '[aria-modal="true"], [role="dialog"]';

type KeyEventLike = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"> & Partial<Pick<KeyboardEvent, "isComposing" | "repeat">>;

export const isFieldTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(FIELD_SELECTOR) !== null;

/** Dialogs open right now. One dialog is one, even when its wrapper and its panel both carry the attributes. */
export const countOpenModals = (root: ParentNode = document): number =>
  Array.from(root.querySelectorAll(MODAL_SELECTOR)).filter((node) => !node.parentElement?.closest(MODAL_SELECTOR)).length;

const isLetterKey = (key: string): boolean => key.length !== 1 || key.toUpperCase() !== key.toLowerCase();

/**
 * `mod` is Cmd on macOS and Ctrl elsewhere; `ctrl` is the Ctrl key itself. Alt must match exactly. Shift must match
 * for letters; for symbol keys ("?", "/") it only counts when the chord asks for it, since the symbol may need Shift.
 */
export const chordMatches = (event: KeyEventLike, chord: KeyChord, mac: boolean): boolean => {
  // Chrome fires a keydown with no `key` when an autofill suggestion is picked.
  if (!event.key || event.key.toLowerCase() !== chord.key.toLowerCase()) return false;
  const wantsMeta = Boolean(chord.mod) && mac;
  const wantsCtrl = (Boolean(chord.mod) && !mac) || Boolean(chord.ctrl);
  if (event.metaKey !== wantsMeta || event.ctrlKey !== wantsCtrl) return false;
  if (event.altKey !== Boolean(chord.alt)) return false;
  if (isLetterKey(chord.key) || chord.shift) return event.shiftKey === Boolean(chord.shift);
  return true;
};

/** A bare character key (WCAG 2.1.4): no Cmd, Ctrl or Alt. */
export const isSingleKeyChord = (chord: KeyChord): boolean => !chord.mod && !chord.ctrl && !chord.alt;

export interface ShortcutLookup {
  mac: boolean;
  /** Single-key shortcuts fire only while this is on. Default on. */
  characterKeys?: boolean;
  inField: boolean;
  /** Any `aria-modal` dialog is open (including our own palette or shortcuts dialog). */
  modalOpen: boolean;
  /** A dialog other than our own palette or shortcuts dialog is open. */
  foreignModalOpen: boolean;
}

/**
 * The action a keydown triggers, or null. Mod+K style shortcuts (`allowInFields`) work in fields; every other chord
 * is ignored in fields. Mod+K style shortcuts yield only to a foreign dialog; every other chord yields to any dialog. `boundElsewhere` shortcuts are never bound here.
 */
export const findShortcutAction = (event: KeyEventLike, actions: readonly CommandAction[], lookup: ShortcutLookup): CommandAction | null => {
  if (event.isComposing || event.repeat) return null;
  for (const action of actions) {
    const shortcut = action.shortcut;
    if (!shortcut || shortcut.boundElsewhere || shortcut.sequence) continue;
    if (lookup.inField && !shortcut.allowInFields) continue;
    if (shortcut.allowInFields ? lookup.foreignModalOpen : lookup.modalOpen) continue;
    const characterKeys = lookup.characterKeys !== false;
    const hit = shortcut.chords.some((chord) => (characterKeys || !isSingleKeyChord(chord)) && chordMatches(event, chord, lookup.mac));
    if (hit) return action;
  }
  return null;
};

/** Whether a bare key (no Cmd, Ctrl or Alt) starts any two-key sequence in `actions`. */
export const startsSequence = (event: KeyEventLike, actions: readonly CommandAction[], mac: boolean): boolean =>
  !event.isComposing && !event.repeat && actions.some((action) => action.shortcut?.sequence && !action.shortcut.boundElsewhere && chordMatches(event, action.shortcut.chords[0], mac));

/** The action whose two-key sequence is `leader` then this key, or null. */
export const findSequenceAction = (leader: KeyChord, event: KeyEventLike, actions: readonly CommandAction[], mac: boolean): CommandAction | null =>
  actions.find((action) => {
    const shortcut = action.shortcut;
    if (!shortcut?.sequence || shortcut.boundElsewhere || shortcut.chords.length < 2) return false;
    return shortcut.chords[0].key === leader.key && chordMatches(event, shortcut.chords[1], mac);
  }) ?? null;
